import { supabase } from "./supabase";

const API_URL = import.meta.env.VITE_API_URL ?? "/api";

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers = new Headers(init.headers);
  if (session?.access_token) {
    headers.set("Authorization", `Bearer ${session.access_token}`);
  }
  headers.set("Content-Type", "application/json");
  return fetch(`${API_URL}${path}`, { ...init, headers, credentials: "include" });
}

async function json<T>(r: Response): Promise<T> {
  const data = await r.json();
  if (!r.ok) throw new Error(data?.error ?? r.statusText);
  return data as T;
}

export const api = {
  me: () => authedFetch("/auth/me").then((r) => json<{ user: any; member: any }>(r)),

  dashboard: {
    state: () => authedFetch("/dashboard/state").then((r) => json<{ member: any; ywi: any; vitals: any; visits: any[]; trend: any[]; plan: any }>(r)),
  },

  coach: {
    chat: (body: { messages: Array<{ role: "user" | "assistant"; content: string }> }) =>
      authedFetch("/coach/chat", { method: "POST", body: JSON.stringify(body) }).then((r) => json<{ reply: string }>(r)),
  },

  wearables: {
    list:       ()                 => authedFetch("/wearables/connections").then((r) => json<{ connections: Array<{ id: string; platform: string; is_active: boolean; connected_at: string; last_sync_at: string | null }> }>(r)),
    connect:    (platform: string) => authedFetch(`/wearables/connect/${platform}`, { method: "POST" }).then((r) => json<{ connection: any; note: string }>(r)),
    disconnect: (platform: string) => authedFetch(`/wearables/connect/${platform}`, { method: "DELETE" }).then((r) => json<{ disconnected: string }>(r)),
    sync:       ()                 => authedFetch("/wearables/sync", { method: "POST" }).then((r) => json<{ synced: number; days: number; primary_platform: string; note: string }>(r)),
  },

  onboarding: {
    complete: (body: {
      first_name: string;
      last_name: string;
      phone?: string;
      timezone?: string;
      tier: "root" | "rise" | "radiate";
      assessment_responses: Record<string, unknown>;
      consents: { lifescribe_basic: boolean; wearable_integration: boolean; ai_coaching: boolean };
    }) =>
      authedFetch("/onboarding/complete", { method: "POST", body: JSON.stringify(body) })
        .then((r) => json<{ onboarding_complete: boolean; initial_ywi: any }>(r)),
  },

  ywi: {
    current:   ()           => authedFetch("/ywi/current").then((r) => json<{ ywi: any }>(r)),
    trend:     (days = 30)  => authedFetch(`/ywi/trend?days=${days}`).then((r) => json<{ trend: any[] }>(r)),
    recompute: ()           => authedFetch("/ywi/recompute", { method: "POST" }).then((r) => json<{ result: any }>(r)),
  },

  checkins: {
    list:   (days = 7) => authedFetch(`/checkins?days=${days}`).then((r) => json<{ checkins: any[] }>(r)),
    create: (body: { mood: number; energy: number; stress: number; sleep_quality?: number; note?: string; context?: string }) =>
      authedFetch("/checkins", { method: "POST", body: JSON.stringify(body) }).then((r) => json<{ checkin: any }>(r)),
  },

  journal: {
    list:   (limit = 20) => authedFetch(`/journal?limit=${limit}`).then((r) => json<{ entries: any[] }>(r)),
    create: (body: { content: string; tags?: string[]; mood_at_time?: number }) =>
      authedFetch("/journal", { method: "POST", body: JSON.stringify(body) }).then((r) => json<{ entry: any }>(r)),
  },

  recommendations: {
    list:     ()         => authedFetch("/recommendations").then((r) => json<{ recommendations: any[] }>(r)),
    markRead: (id: string) => authedFetch(`/recommendations/${id}/read`, { method: "POST" }).then((r) => json<{ ok: boolean }>(r)),
  },

  history: {
    readings: (days = 30) => authedFetch(`/history/readings?days=${days}`).then((r) => json<{ readings: any[]; days: number }>(r)),
    activity: (days = 60) => authedFetch(`/history/activity?days=${days}`).then((r) => json<{ activity: any[]; days: number }>(r)),
  },

  pain: {
    active:  ()           => authedFetch("/pain/active").then((r) => json<{ points: any[] }>(r)),
    add:     (body: { x_norm: number; y_norm: number; level: number; description?: string; region?: string }) =>
      authedFetch("/pain", { method: "POST", body: JSON.stringify(body) }).then((r) => json<{ point: any }>(r)),
    remove:  (id: string) => authedFetch(`/pain/${id}`, { method: "DELETE" }).then((r) => json<{ ok: boolean; id: string }>(r)),
    history: (days = 90)  => authedFetch(`/pain/history?days=${days}`).then((r) => json<{ incidents: any[]; days: number }>(r)),
  },

  ops: {
    me:       ()          => authedFetch("/ops/me").then((r) => json<{ roles: string[]; isStaff: boolean; isAdmin: boolean }>(r)),
    roster:   ()          => authedFetch("/ops/roster").then((r) => json<{ roster: any[] }>(r)),
    metrics:  ()          => authedFetch("/ops/metrics").then((r) => json<any>(r)),
    activity: (days = 30) => authedFetch(`/ops/activity?days=${days}`).then((r) => json<any>(r)),
  },

  practitioner: {
    me:         ()          => authedFetch("/practitioner/me").then((r) => json<{ isPractitioner: boolean }>(r)),
    members:    ()          => authedFetch("/practitioner/members").then((r) => json<{ members: any[] }>(r)),
    member:     (id: string) => authedFetch(`/practitioner/member/${id}`).then((r) => json<any>(r)),
    notes:      (id: string) => authedFetch(`/practitioner/member/${id}/notes`).then((r) => json<{ notes: any[] }>(r)),
    addNote:    (id: string, body: { text: string; session_date?: string; tags?: string[] }) =>
      authedFetch(`/practitioner/member/${id}/notes`, { method: "POST", body: JSON.stringify(body) }).then((r) => json<{ note: any }>(r)),
    referrals:  ()          => authedFetch("/practitioner/referrals").then((r) => json<{ referrals: any[] }>(r)),
    colleagues: ()          => authedFetch("/practitioner/colleagues").then((r) => json<{ colleagues: any[] }>(r)),
    addReferral:(body: { member_id: string; to_practitioner: string; reason: string; modality_suggested?: string }) =>
      authedFetch("/practitioner/referrals", { method: "POST", body: JSON.stringify(body) }).then((r) => json<{ referral: any }>(r)),
    respondReferral: (id: string, status: "accepted" | "declined" | "completed") =>
      authedFetch(`/practitioner/referrals/${id}`, { method: "PATCH", body: JSON.stringify({ status }) }).then((r) => json<{ referral: any }>(r)),
  },
};
